import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, InputCheckbox, InputText, InputToggle, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import jiraLogo from "~/assets/i/apps/atlassian_jira_logo.svg";
import { getJiraSpaceUrl, handleError } from "~/lib/helpers";
import styled from "styled-components";
import Markdown from "~/components/Markdown";
import { useTranslation } from "react-i18next";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledPicture = styled(Picture)`
  ${_ref => {
  let {
    char,
    theme
  } = _ref;
  return !!char && `background: ${theme.colors.primary.main};
  color: ${theme.colors.background.primary};
  &:before {
    content: "${char}";
  }`;
}}
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const JiraIntegrationCard = _ref2 => {
  let {
    ...rest
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.JIRA));
  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [jiraProjectsError, setJiraProjectsError] = useState();
  const [jiraFields, setJiraFields] = useState([]);
  const [loadingJiraFields, setLoadingJiraFields] = useState(false);
  const [jiraFieldsError, setJiraFieldsError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [searchFields, setSearchFields] = useState("");
  const handleLoadJiraProjects = () => {
    setLoadingJiraProjects(true);
    setJiraProjectsError(undefined);
    api.fetchJiraProjects().then(data => setJiraProjects(data)).catch(e => setJiraProjectsError(handleError(e))).finally(() => setLoadingJiraProjects(false));
  };
  const handleLoadJiraFields = () => {
    setLoadingJiraFields(true);
    setJiraFieldsError(undefined);
    api.fetchJiraFields({
      filter: {
        isCustom: true
      }
    }).then(data => setJiraFields(data)).catch(e => setJiraFieldsError(handleError(e))).finally(() => setLoadingJiraFields(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadJiraProjects();
      handleLoadJiraFields();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.jiraInstall().then(_ref3 => {
        let {
          path
        } = _ref3;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Jira", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdateProjects = function (projectIds) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let projects = integration.settings?.projects || [];
      projectIds.forEach(projectId => {
        if (projects.includes(projectId) && !force || deselect && force) {
          projects = projects.filter(id => id !== projectId);
        } else {
          projects.push(projectId);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          projects
        }
      }));
    }
  };
  const handleUpdateFields = function (fields) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let customFields = integration.settings?.customFields || [];
      fields.forEach(field => {
        if (customFields.find(f => f.id === field.id) && !force || deselect && force) {
          customFields = customFields.filter(cf => cf.id !== field.id);
        } else {
          customFields.push(field);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          customFields
        }
      }));
    }
  };
  const handleSearch = value => {
    setSearch(value);
  };
  const handleSearchFields = value => {
    setSearchFields(value);
  };
  const filteredProjects = jiraProjects.filter(project => project.name.toLowerCase().includes(search.toLowerCase()) || project.key.toLowerCase().includes(search.toLowerCase()) || project.description && project.description.toLowerCase().includes(search.toLowerCase()));
  const hasFiltered = filteredProjects.length && search;
  const filteredFields = jiraFields.filter(field => field.name.toLowerCase().includes(searchFields.toLowerCase()) || field.id.toLowerCase().includes(searchFields.toLowerCase()));
  const hasFilteredFields = filteredFields.length && search;
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: jiraLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Atlassian Jira"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), !!integration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
    display: "block",
    fontSize: 4,
    lineHeight: 6,
    linkColor: "primary",
    s: {
      mb: 3
    },
    text: `Connected to [${integration?.settings?.cloud[0]?.name}](${getJiraSpaceUrl(integration)}) Space. Select Project(s) and Custom Field(s) you want us to subscribe and map.`
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, "Projects"), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: search,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => handleSearch(val),
    elemAfter: !!search && /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateProjects(filteredProjects.map(r => r.key), true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateProjects(filteredProjects.map(r => r.key), true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), jiraProjectsError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, jiraProjectsError), loadingJiraProjects ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : filteredProjects.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 4,
    template: "1rem 3rem 1fr auto",
    s: {
      pl: 2,
      pr: 2
    },
    gap: 3,
    justify: "center",
    fullWidth: true
  }, filteredProjects.map(project => /*#__PURE__*/React.createElement(React.Fragment, {
    key: project.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.projects?.includes(project.key),
    onChange: () => handleUpdateProjects([project.key]),
    s: {
      mt: 1
    }
  }), /*#__PURE__*/React.createElement(StyledPicture, {
    width: "3rem",
    height: "3rem",
    src: project.avatar,
    char: project.avatar ? undefined : project.key,
    color: "translucent"
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      m: 0
    }
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    s: {
      pt: 1
    },
    weight: "semiBold"
  }, project.name, " (", project.key, ")"), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    color: "textSecondary"
  }, project.description || "No description")), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center",
    justify: "end"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: project.isPrivate ? "translucentWarning" : "translucentPrimary",
    s: {
      px: 2.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: project.isPrivate ? "lock" : "people",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), project.isPrivate ? "Private" : "Public"))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      my: 2
    },
    align: "center"
  }, "No projects found"), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3,
      mt: 5
    }
  }, "Custom Fields"), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: searchFields,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => handleSearchFields(val),
    elemAfter: !!searchFields && /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateFields(filteredFields, true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFilteredFields ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateFields(filteredFields, true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFilteredFields ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), jiraFieldsError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, jiraFieldsError), loadingJiraFields ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : filteredFields.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 4,
    template: "1rem 1fr",
    s: {
      pl: 2,
      pr: 2
    },
    gap: 3,
    justify: "center",
    fullWidth: true
  }, filteredFields.map(field => /*#__PURE__*/React.createElement(React.Fragment, {
    key: field.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: !!integration?.settings?.customFields?.find(f => f.id === field.id),
    onChange: () => handleUpdateFields([field]),
    s: {
      mt: -1
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      m: 0
    }
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    s: {
      pt: 1
    },
    weight: "semiBold"
  }, field.name))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      my: 2
    },
    align: "center"
  }, "No custom fields found")));
};
export default JiraIntegrationCard;